import { DateTime } from "luxon";
import { z } from "zod";
import { callDirectionSchema } from "./callDirection.js";
const playbookTestCustomerMode = {
  Simple: "simple",
  Detailed: "detailed"
};
const allPlaybookTestCustomerModes = [
  playbookTestCustomerMode.Simple,
  playbookTestCustomerMode.Detailed
];
const playbookTestCustomerModeSchema = z.enum(
  allPlaybookTestCustomerModes
);
const playbookTestFactDisclosure = {
  Volunteered: "volunteered",
  OnRequest: "on_request",
  Reluctant: "reluctant",
  Corrected: "corrected",
  Refused: "refused"
};
const allPlaybookTestFactDisclosures = [
  playbookTestFactDisclosure.Volunteered,
  playbookTestFactDisclosure.OnRequest,
  playbookTestFactDisclosure.Reluctant,
  playbookTestFactDisclosure.Corrected,
  playbookTestFactDisclosure.Refused
];
const playbookTestFactDisclosureSchema = z.enum(
  allPlaybookTestFactDisclosures
);
const playbookTestSpeechStyleSchema = z.object({
  /**
   * Every number is measured over one real call's customer turns; null when
   * the case was written by hand.
   */
  turn_shape: z.object({
    median_words: z.number().nonnegative(),
    p25_words: z.number().nonnegative(),
    p75_words: z.number().nonnegative(),
    p90_words: z.number().nonnegative(),
    short_share: z.number().min(0).max(1),
    long_share: z.number().min(0).max(1),
    spoken_marker_share: z.number().min(0).max(1),
    multi_question_share: z.number().min(0).max(1)
  }).nullable(),
  /** Register instructions with a quoted example each. */
  style_manual: z.array(z.object({ command: z.string(), example: z.string() })),
  /** Sampled turns the customer imitates; quoted from anonymized calls only. */
  exchanges: z.array(
    z.object({ rep: z.string().nullable(), customer: z.string() })
  )
});
const playbookTestCustomerFactSchema = z.object({
  fact: z.string(),
  /** The concrete thing they hold, in their own words. */
  value: z.string(),
  disclosure: playbookTestFactDisclosureSchema,
  /** The phase they first say it in; null for no constraint. */
  phase: z.string().nullable()
});
const playbookTestCustomerSchema = z.object({
  /** What they are trying to get out of the call; null when they have no goal. */
  goal: z.string().nullable(),
  temperament: z.string(),
  decision_style: z.string(),
  situations: z.array(z.object({ label: z.string(), description: z.string() })),
  /** How this kind of call tends to run, in a sentence or two. */
  arc: z.string(),
  facts: z.array(playbookTestCustomerFactSchema),
  objections: z.array(
    z.object({ objection: z.string(), phase: z.string().nullable() })
  ),
  conversion_conditions: z.array(z.string()),
  speech_style: playbookTestSpeechStyleSchema.nullable()
});
const playbookTestCallSetupSchema = z.object({
  direction: callDirectionSchema,
  contact: z.object({
    name: z.string().nullable(),
    email: z.string().nullable(),
    phone_number: z.string().nullable()
  }),
  contact_custom_fields: z.array(
    z.object({ custom_field_definition_id: z.uuid(), value: z.string() })
  ),
  interaction_custom_fields: z.array(
    z.object({ custom_field_definition_id: z.uuid(), value: z.string() })
  ),
  /**
   * When the call takes place, with the offset of the company's timezone. The
   * agent, the simulated customer, the tool mocks and the grading all read it
   * as the current time, so a date the case or its mocks fix stays valid on
   * every run. Absent, the call runs on the real clock.
   */
  simulated_at: z.iso.datetime({ offset: true }).optional()
});
function readPlaybookTestCallSetupNow(callSetup) {
  return callSetup?.simulated_at ? DateTime.fromISO(callSetup.simulated_at, { setZone: true }) : null;
}
const playbookTestToolMockSchema = z.object({
  tool_id: z.string(),
  /** Returned to the agent verbatim. */
  response: z.string(),
  /** Returned as a tool error instead of a result. */
  is_error: z.boolean().optional()
});
const playbookTestAssertionBaseSchema = z.object({
  id: z.uuid(),
  /** Judged and shown, but never gating the case's verdict. */
  diagnostic: z.boolean().optional()
});
const playbookTestAssertionKind = {
  Outcome: "outcome",
  ToolCall: "tool_call",
  FactCaptured: "fact_captured",
  Content: "content"
};
const allPlaybookTestAssertionKinds = [
  playbookTestAssertionKind.Outcome,
  playbookTestAssertionKind.ToolCall,
  playbookTestAssertionKind.FactCaptured,
  playbookTestAssertionKind.Content
];
const playbookTestAssertionKindSchema = z.enum(
  allPlaybookTestAssertionKinds
);
const playbookTestOutcomeAssertionSchema = playbookTestAssertionBaseSchema.extend({
  kind: z.literal(playbookTestAssertionKind.Outcome),
  outcome: z.string()
});
const playbookTestToolCallAssertionSchema = playbookTestAssertionBaseSchema.extend({
  kind: z.literal(playbookTestAssertionKind.ToolCall),
  tool_id: z.string(),
  expected: z.enum(["called", "not_called"]),
  /** Constraints on the arguments, because calling the tool is not enough. */
  arguments: z.array(
    z.object({
      path: z.string(),
      equals: z.string().optional(),
      matches: z.string().optional()
    })
  ).optional()
});
const playbookTestFactCapturedAssertionSchema = playbookTestAssertionBaseSchema.extend({
  kind: z.literal(playbookTestAssertionKind.FactCaptured),
  fact: z.string()
});
const playbookTestContentScope = {
  Speech: "speech",
  Call: "call"
};
const playbookTestContentAssertionSchema = playbookTestAssertionBaseSchema.extend({
  kind: z.literal(playbookTestAssertionKind.Content),
  statement: z.string(),
  /** `absent` is how a guardrail is written. */
  expected: z.enum(["present", "absent"]),
  /** Absent reads as `speech`, which is what every stored assertion meant. */
  scope: z.enum([playbookTestContentScope.Speech, playbookTestContentScope.Call]).optional()
});
const playbookTestAssertionSchema = z.discriminatedUnion("kind", [
  playbookTestOutcomeAssertionSchema,
  playbookTestToolCallAssertionSchema,
  playbookTestFactCapturedAssertionSchema,
  playbookTestContentAssertionSchema
]);
const playbookTestAssertionsSchema = z.array(playbookTestAssertionSchema).refine(
  (assertions) => new Set(assertions.map((assertion) => assertion.id)).size === assertions.length,
  { message: "Each assertion needs its own id" }
);
const playbookTestOriginSchema = z.object({
  source: z.enum(["generated", "authored"]),
  profile: z.string().nullable(),
  scenario_label: z.string().nullable(),
  aggregate_content_hash: z.string().nullable(),
  draw_content_hash: z.string().optional(),
  seed: z.string().nullable(),
  generated_at: z.iso.datetime().nullable()
});
export {
  allPlaybookTestAssertionKinds,
  allPlaybookTestCustomerModes,
  allPlaybookTestFactDisclosures,
  playbookTestAssertionKind,
  playbookTestAssertionKindSchema,
  playbookTestAssertionSchema,
  playbookTestAssertionsSchema,
  playbookTestCallSetupSchema,
  playbookTestContentScope,
  playbookTestCustomerFactSchema,
  playbookTestCustomerMode,
  playbookTestCustomerModeSchema,
  playbookTestCustomerSchema,
  playbookTestFactDisclosure,
  playbookTestFactDisclosureSchema,
  playbookTestOriginSchema,
  playbookTestSpeechStyleSchema,
  playbookTestToolMockSchema,
  readPlaybookTestCallSetupNow
};
//# sourceMappingURL=playbookTestCase.js.map
